/**
 * AI Insights Service
 *
 * Generates AI-powered insights for dashboards using Claude through the privacy gateway.
 * Features:
 * - Structured JSON output contract with Zod validation
 * - Session-based caching with configurable TTL
 * - Graceful fallback to static insights on error
 * - Explicit privacy: only anonymized metrics sent to AI
 */

import { z } from "zod";

import type {
  PlatformMetrics,
  ActivityItem,
} from "@/services/analyticsService";

import { generateAnalyticsSummary } from "./privacyAwareAIService";

// ============================================================================
// Feature Flag
// ============================================================================

export const AI_INSIGHTS_ENABLED =
  (import.meta.env.VITE_ENABLE_AI_INSIGHTS as string) === "true";

// ============================================================================
// Types and Schemas
// ============================================================================

/**
 * Insight types - determines icon and styling in UI
 */
export type InsightType = "optimization" | "anomaly" | "opportunity" | "alert";

/**
 * Priority levels - affects sort order and visual prominence
 */
export type InsightPriority = "low" | "medium" | "high";

/**
 * Zod schema for validating AI-generated insights
 * This is the contract that Claude's output must match
 */
export const AIInsightSchema = z.object({
  id: z.string(),
  type: z.enum(["optimization", "anomaly", "opportunity", "alert"]),
  title: z.string().max(100),
  description: z.string().max(500),
  action: z.string().max(50).optional(),
  priority: z.enum(["low", "medium", "high"]),
});

export const AIInsightsResponseSchema = z.object({
  insights: z.array(AIInsightSchema).max(5),
});

export type AIInsight = z.infer<typeof AIInsightSchema>;
export type AIInsightsResponse = z.infer<typeof AIInsightsResponseSchema>;

// ============================================================================
// Cache Configuration
// ============================================================================

interface CacheEntry {
  insights: AIInsight[];
  timestamp: number;
  clientId: string;
  metricsHash: string;
}

const CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes
const SESSION_CACHE_KEY = "nexus_ai_insights_cache";

/**
 * Generate a hash of metrics to detect when data has changed
 */
function hashMetrics(metrics: PlatformMetrics): string {
  return `${metrics.totalClients}-${metrics.activeCampaigns}-${metrics.totalDonors}-${Math.round(metrics.monthlyRevenue)}`;
}

/**
 * Get cached insights from sessionStorage (user-scoped via session)
 */
function getCachedInsights(
  clientId: string,
  metricsHash: string,
): AIInsight[] | null {
  try {
    const cached = sessionStorage.getItem(SESSION_CACHE_KEY);
    if (!cached) return null;

    const entry: CacheEntry = JSON.parse(cached);

    // Validate cache is for same client and hasn't expired
    if (
      entry.clientId !== clientId ||
      entry.metricsHash !== metricsHash ||
      Date.now() - entry.timestamp > CACHE_TTL_MS
    ) {
      sessionStorage.removeItem(SESSION_CACHE_KEY);
      return null;
    }

    return entry.insights;
  } catch {
    return null;
  }
}

/**
 * Store insights in sessionStorage
 */
function cacheInsights(
  clientId: string,
  metricsHash: string,
  insights: AIInsight[],
): void {
  try {
    const entry: CacheEntry = {
      insights,
      timestamp: Date.now(),
      clientId,
      metricsHash,
    };
    sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(entry));
  } catch {
    // sessionStorage might be full or unavailable
  }
}

// ============================================================================
// Static Fallback Insights
// ============================================================================

/**
 * Static insights shown when AI is disabled or fails
 * These are generic but still useful prompts
 */
export const STATIC_INSIGHTS: AIInsight[] = [
  {
    id: "static-1",
    type: "optimization",
    title: "Review campaign performance",
    description:
      "Check which campaigns are performing best and consider applying their strategies to others.",
    action: "View Campaigns",
    priority: "medium",
  },
  {
    id: "static-2",
    type: "opportunity",
    title: "Identify upgrade candidates",
    description:
      "Look for donors who have increased giving frequency - they may be ready for a larger ask.",
    action: "See Prospects",
    priority: "medium",
  },
  {
    id: "static-3",
    type: "alert",
    title: "Monitor donor engagement",
    description:
      "Regularly review donor activity to identify at-risk supporters before they lapse.",
    action: "View Analysis",
    priority: "low",
  },
];

// ============================================================================
// AI Prompt Construction
// ============================================================================

/**
 * Build the system prompt for insight generation
 */
function buildInsightSystemPrompt(): string {
  return `You are a nonprofit fundraising analyst assistant. Generate actionable insights based on the provided metrics.

CRITICAL: You MUST respond with ONLY a valid JSON object matching this exact schema:
{
  "insights": [
    {
      "id": "unique-string-id",
      "type": "optimization" | "anomaly" | "opportunity" | "alert",
      "title": "Brief title (max 100 chars)",
      "description": "Detailed explanation (max 500 chars)",
      "action": "Short action label (max 50 chars, optional)",
      "priority": "low" | "medium" | "high"
    }
  ]
}

Rules:
- Generate 2-4 insights based on the metrics
- Each insight must be actionable and specific
- Use "optimization" for ways to improve existing efforts
- Use "anomaly" for unusual patterns that need attention
- Use "opportunity" for growth potential
- Use "alert" for urgent issues or risks
- Do NOT include any text outside the JSON object
- Do NOT include markdown formatting`;
}

/**
 * Build the anonymized metrics payload
 * Only includes aggregated numbers, no PII
 */
function buildAnonymizedPayload(
  metrics: PlatformMetrics,
  recentActivity: ActivityItem[],
): Record<string, unknown> {
  // Count activity types (no names, emails, or IDs)
  const activityCounts = recentActivity.reduce(
    (acc, item) => {
      acc[item.type] = (acc[item.type] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  return {
    // Platform metrics (all aggregated/anonymized)
    totalClients: metrics.totalClients,
    activeCampaigns: metrics.activeCampaigns,
    totalDonors: metrics.totalDonors,
    monthlyRevenue: Math.round(metrics.monthlyRevenue), // Round to avoid float precision
    avgGiftSize: Math.round(metrics.avgGiftSize),
    donorsChangePercent: metrics.donorsChange,
    revenueChangePercent: metrics.revenueChange,

    // Activity counts (no specifics)
    recentActivityCounts: activityCounts,
    totalRecentActivity: recentActivity.length,
  };
}

// ============================================================================
// Main Service Function
// ============================================================================

/**
 * Generate dashboard insights using Claude
 *
 * @param clientId - Current client ID (for cache key)
 * @param metrics - Platform metrics (anonymized before sending)
 * @param recentActivity - Recent activity items (counts only)
 * @returns Array of AI-generated insights or fallback static insights
 */
export async function generateDashboardInsights(
  clientId: string,
  metrics: PlatformMetrics,
  recentActivity: ActivityItem[],
): Promise<AIInsight[]> {
  // Feature flag check
  if (!AI_INSIGHTS_ENABLED) {
    return STATIC_INSIGHTS;
  }

  const metricsHash = hashMetrics(metrics);

  // Check cache first
  const cached = getCachedInsights(clientId, metricsHash);
  if (cached) {
    return cached;
  }

  try {
    // Build anonymized payload
    const anonymizedData = buildAnonymizedPayload(metrics, recentActivity);

    // Call Claude through privacy gateway
    const response = await generateAnalyticsSummary(
      "dashboard_insights",
      anonymizedData,
      buildInsightSystemPrompt(),
    );

    if (!response.ok || !response.content) {
      console.warn("AI insights generation failed:", response.error);
      return STATIC_INSIGHTS;
    }

    // Parse and validate JSON response
    let parsed: unknown;
    try {
      // Try to extract JSON from the response (Claude might add extra text)
      const jsonMatch = response.content.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error("No JSON object found in response");
      }
      parsed = JSON.parse(jsonMatch[0]);
    } catch (parseError) {
      console.warn("Failed to parse AI response as JSON:", parseError);
      return STATIC_INSIGHTS;
    }

    // Validate with Zod schema
    const validated = AIInsightsResponseSchema.safeParse(parsed);
    if (!validated.success) {
      console.warn("AI response failed schema validation:", validated.error);
      return STATIC_INSIGHTS;
    }

    const insights = validated.data.insights;

    // Cache successful results
    cacheInsights(clientId, metricsHash, insights);

    return insights;
  } catch (error) {
    console.error("Error generating AI insights:", error);
    return STATIC_INSIGHTS;
  }
}

/**
 * Clear cached insights (useful when user wants fresh data)
 */
export function clearInsightsCache(): void {
  try {
    sessionStorage.removeItem(SESSION_CACHE_KEY);
  } catch {
    // Ignore errors
  }
}

/**
 * Check if insights are currently cached
 */
export function hasValidCache(
  clientId: string,
  metrics: PlatformMetrics,
): boolean {
  const metricsHash = hashMetrics(metrics);
  return getCachedInsights(clientId, metricsHash) !== null;
}
